// Optimized Service for handling customer reservations with performance improvements
// Supports both B2B (golf course management) and B2C (golfer booking) environments
import { getApiBaseUrl } from './api';
import {
  customerTypeIsMemberFacing,
  customerTypeIsPublicGuestOnlineBooking,
  customerTypeShowsIndividualNameOnTeeSheet,
  customerTypeCanBookOnline,
} from './customer-type-online-booking';
import { publicBookingApiParamsObjectFromWindow, publicBookingApiParamsFromWindow } from '../utils/club-route-key';
import { reservationRosterPlayers } from '../utils/reservation-roster';
// Use function to get API URL dynamically (detects mobile vs desktop)
const getApiBase = () => getApiBaseUrl();

/** Failed reservation / message API response with JSON `{ message?, error?, code? }` (matches API error handler). */
export class ReservationsApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ReservationsApiError';
    this.status = status;
    this.code = code;
  }
}

async function parseFailedResponseBody(
  response: Response,
  fallback: string
): Promise<{ message: string; code?: string }> {
  let message = fallback;
  let code: string | undefined;
  try {
    const text = await response.text();
    if (!text.trim()) return { message, code };
    const errBody = JSON.parse(text) as Record<string, unknown>;
    if (typeof errBody.message === 'string' && errBody.message.trim()) {
      message = errBody.message.trim();
    } else if (typeof errBody.error === 'string' && errBody.error.trim()) {
      message = errBody.error.trim();
    }
    if (typeof errBody.code === 'string' && errBody.code.trim()) {
      code = errBody.code.trim();
    }
  } catch {
    /* keep fallback */
  }
  return { message, code };
}

/** Row from POST /pricing/tee-times for the customer type that matches the displayed (lowest) price. */
function pickPricingEntryForPrice(
  teeTimePricing: { pricing?: Record<string, any> },
  customerTypes: Array<{ id: string }>,
  targetPrice: number
): any | null {
  if (!teeTimePricing?.pricing) return null;
  for (const ct of customerTypes) {
    const p = teeTimePricing.pricing[ct.id];
    if (p?.template && Math.abs(Number(p.finalPrice) - targetPrice) < 0.02) {
      return p;
    }
  }
  return null;
}

/** Matches API `buildReservationTeeTimeId` for POST /reservations (incl. optional `|laneKey`). */
export function buildReservationTeeTimeIdForApi(slot: {
  date: string;
  time: string;
  laneKey?: string | null;
}): string {
  const DEFAULT_LANE = 'default';
  const rawTime = String(slot.time);
  const m = rawTime.match(/^(\d{1,2}):(\d{2})/);
  const hm = m ? `${m[1].padStart(2, '0')}:${m[2]}` : rawTime.slice(0, 5);
  const lane = slot.laneKey && slot.laneKey !== DEFAULT_LANE ? `|${slot.laneKey}` : '';
  return `${slot.date}T${hm}${lane}`;
}

/** Compare template base (before demand rules) to final price; omit when unchanged or not applicable. */
/** Staff tee-sheet promo persisted on `generated_tee_times.promotion` (public GET includes it). */
function normalizeStaffPromotion(raw: unknown):
  | {
      type: 'percentage' | 'fixed';
      value: number;
      discountGreenFee?: boolean;
      discountCart?: boolean;
    }
  | null {
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    try {
      obj = JSON.parse(t);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== 'object') return null;
  const p = obj as Record<string, unknown>;
  const typeStr =
    typeof p.type === 'string' ? p.type.toLowerCase().trim() : '';
  if (typeStr !== 'percentage' && typeStr !== 'fixed') return null;
  const val = Number(p.value);
  if (!(val > 0)) return null;
  return {
    type: typeStr as 'percentage' | 'fixed',
    value: val,
    discountGreenFee: p.discountGreenFee !== false,
    discountCart: p.discountCart !== false,
  };
}

function buildDynamicPricingImpact(pricingEntry: any):
  | {
      direction: 'lower' | 'higher';
      percentRounded: number;
      underOnePercent: boolean;
    }
  | undefined {
  if (!pricingEntry?.template) return undefined;
  const base = Number(pricingEntry.basePrice);
  const fin = Number(pricingEntry.finalPrice);
  if (!(base > 0) || fin <= 0) return undefined;
  const delta = fin - base;
  if (Math.abs(delta) < 0.01) return undefined;
  const pct = (Math.abs(delta) / base) * 100;
  const underOnePercent = pct > 0 && pct < 1;
  const percentRounded = underOnePercent ? 0 : Math.min(999, Math.round(pct));
  return {
    direction: delta < 0 ? 'lower' : 'higher',
    percentRounded,
    underOnePercent,
  };
}

export interface Reservation {
  id: string;
  teeTimeId: string;
  courseId: string;
  customerId: string;
  playerCount: number;
  status: string;
  /** Mirrors Prisma `paymentStatus` — staff may show PAID while `status` is still `reserved`. */
  paymentStatus?: string;
  paidAt?: string | null;
  paymentMethod?: string | null;
  paymentTransactionId?: string | null;
  notes?: string;
  source: string;
  channel: string;
  version: number; // Added for optimistic locking
  createdAt: string;
  updatedAt: string;
  course?: {
    id: string;
    name: string;
  };
  /** Set by API when `teeTimeId` is a generated-slot id — use for display/sorting when raw `teeTimeId` is not ISO. */
  teeTimeDate?: string | null;
  teeTimeSlot?: {
    slotDate?: string;
    startTs?: string | Date;
    holeSet?: string;
    laneKey?: string;
  } | null;
  players?: Array<{
    id: string;
    spotNumber: number;
    isGuest: boolean;
    guestName?: string;
    /** Used with paymentMethod so golfer dashboard can mirror staff tee sheet “paid”. */
    isPaid?: boolean | null;
    paymentMethod?: string | null;
    customer?: {
      id: string;
      firstName: string;
      lastName: string;
    };
  }>;
}

export interface PaginatedReservations {
  reservations: Reservation[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// Cache configuration
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class ReservationCache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  invalidate(pattern: string): void {
    const keys = Array.from(this.cache.keys());
    keys.forEach(key => {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    });
  }

  clear(): void {
    this.cache.clear();
  }
}

// Utility functions
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries) throw error;
      
      const delay = baseDelay * Math.pow(2, attempt - 1);
      console.warn(`Attempt ${attempt} failed, retrying in ${delay}ms...`);
      await sleep(delay);
    }
  }
  throw new Error('Max retries exceeded');
};

// Environment detection for B2B vs B2C optimizations
const isB2BEnvironment = (): boolean => {
  // Check for B2B indicators - could be based on URL, user role, or environment variable
  const url = window.location.href;
  const userRole = localStorage.getItem('user_role');
  
  return (
    url.includes('/admin') || 
    url.includes('/management') || 
    userRole === 'staff' || 
    userRole === 'admin' ||
    process.env.NODE_ENV === 'b2b'
  );
};

// Get environment-specific configuration
const getEnvironmentConfig = () => {
  const isB2B = isB2BEnvironment();
  
  return {
    cache: {
      reservationsTTL: isB2B ? 3 * 60 * 1000 : 5 * 60 * 1000, // B2B needs more frequent updates
      teeTimesTTL: isB2B ? 30 * 1000 : 60 * 1000, // B2B needs real-time tee time data
    },
    api: {
      defaultPageSize: isB2B ? 50 : 20, // Larger page sizes for B2B
      maxBatchSize: isB2B ? 100 : 50, // Larger batches for B2B
      requestTimeout: isB2B ? 15000 : 10000, // Longer timeout for B2B operations
    },
    debounce: {
      searchDelay: isB2B ? 200 : 300, // Faster response for B2B
      apiDelay: isB2B ? 100 : 300, // Less debouncing for B2B
    }
  };
};

class OptimizedReservationsService {
  private cache = new ReservationCache();
  private readonly config = getEnvironmentConfig();
  /** Fresh course flags from public API (guest default, preload cart) — short TTL so admin changes apply without stale React Query. */
  private coursePolicyCache: {
    key: string;
    fetchedAt: number;
    byCourseId: Record<
      string,
      { defaultGuestTypeId?: string | null; preloadHalfCartWithGreenFee: boolean }
    >;
  } | null = null;
  private static readonly COURSE_POLICY_TTL_MS = 8_000;

  private async getCoursePoliciesByClubParams(
    clubParams: URLSearchParams,
  ): Promise<
    Record<string, { defaultGuestTypeId?: string | null; preloadHalfCartWithGreenFee: boolean }>
  > {
    if ([...clubParams.keys()].length === 0) return {};
    const key = clubParams.toString();
    const now = Date.now();
    if (
      this.coursePolicyCache &&
      this.coursePolicyCache.key === key &&
      now - this.coursePolicyCache.fetchedAt < OptimizedReservationsService.COURSE_POLICY_TTL_MS
    ) {
      return this.coursePolicyCache.byCourseId;
    }
    try {
      const url = `/golf-clubs/public/courses-with-templates?${clubParams.toString()}`;
      const cr = await this.fetchWithTimeout(url);
      if (!cr.ok) return {};
      const list = await cr.json();
      const byCourseId: Record<
        string,
        { defaultGuestTypeId?: string | null; preloadHalfCartWithGreenFee: boolean }
      > = {};
      if (Array.isArray(list)) {
        for (const c of list) {
          if (c?.id) {
            byCourseId[c.id] = {
              defaultGuestTypeId: c.defaultGuestTypeId ?? null,
              preloadHalfCartWithGreenFee: !!c.preloadHalfCartWithGreenFee,
            };
          }
        }
      }
      this.coursePolicyCache = { key, fetchedAt: now, byCourseId };
      return byCourseId;
    } catch (e) {
      console.warn('[getTeeTimeAvailability] course policy fetch failed', e);
      return {};
    }
  }
  
  private getAuthHeaders(): Record<string, string> {
      const sessionData = localStorage.getItem('booking_service_auth');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
      
      if (sessionData) {
        try {
          const session = JSON.parse(sessionData);
          if (session.token) {
          headers['Authorization'] = `Bearer ${session.token}`;
          }
        } catch (error) {
          console.error('Error parsing session data:', error);
        }
      }
      
    return headers;
  }

  private async fetchWithTimeout(url: string, options: RequestInit = {}): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.api.requestTimeout);
    
    // Add base URL if the URL doesn't start with http
    const fullUrl = url.startsWith('http') ? url : `${getApiBase()}${url.startsWith('/') ? '' : '/'}${url}`;
    
    try {
      const response = await fetch(fullUrl, {
        ...options,
        signal: controller.signal,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers,
        },
      });
      
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  // Get paginated reservations for the signed-in customer (customer JWT route)
  getMyReservations = async (
    page: number = 1,
    limit: number = this.config.api.defaultPageSize,
    status?: string,
    forceRefresh: boolean = false
  ): Promise<PaginatedReservations> => {
    const cacheKey = `my_reservations_${page}_${limit}_${status || 'all'}`;

    if (!forceRefresh) {
      const cached = this.cache.get<PaginatedReservations>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(status && { status }),
      });

      const response = await retryWithBackoff(
        () => this.fetchWithTimeout(`/customer-auth/reservations?${queryParams}`)
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch reservations: ${response.statusText}`);
      }

      const data = await response.json();
      let result: PaginatedReservations;

      if (data.reservations && (data.pagination || data.total != null)) {
        result = {
          reservations: data.reservations,
          total: data.total || data.pagination?.total || 0,
          page: data.page || data.pagination?.page || 1,
          limit: data.limit || data.pagination?.limit || this.config.api.defaultPageSize,
          hasMore: data.hasNext || data.pagination?.hasMore || false,
        };
      } else if (Array.isArray(data)) {
        result = {
          reservations: data,
          total: data.length,
          page: 1,
          limit: data.length,
          hasMore: false,
        };
      } else {
        result = {
          reservations: [],
          total: 0,
          page: 1,
          limit: this.config.api.defaultPageSize,
          hasMore: false,
        };
      }

      this.cache.set(cacheKey, result, this.config.cache.reservationsTTL);
      return result;
    } catch (error) {
      console.error('Error fetching my reservations:', error);
      if (import.meta.env.DEV) {
        const mockReservations = getMockReservations('me');
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        return {
          reservations: mockReservations.slice(startIndex, endIndex),
          total: mockReservations.length,
          page,
          limit,
          hasMore: endIndex < mockReservations.length,
        };
      }
      throw error;
    }
  };

  // Get paginated reservations for a specific customer
  getCustomerReservations = async (
    customerId: string,
    page: number = 1,
    limit: number = this.config.api.defaultPageSize,
    status?: string,
    forceRefresh: boolean = false
  ): Promise<PaginatedReservations> => {
    const cacheKey = `reservations_${customerId}_${page}_${limit}_${status || 'all'}`;
    
    // Check cache first
    if (!forceRefresh) {
      const cached = this.cache.get<PaginatedReservations>(cacheKey);
      if (cached) {
        console.log('📦 Returning cached reservations for customer:', customerId);
        return cached;
      }
    }

    try {
      console.log('🔍 Fetching reservations for customer:', customerId, `(page ${page})`);
      
      const queryParams = new URLSearchParams({
        customerId,
        page: page.toString(),
        limit: limit.toString(),
        ...(status && { status }),
      });

      const response = await retryWithBackoff(
        () => this.fetchWithTimeout(`/reservations?${queryParams}`)
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch reservations: ${response.statusText}`);
      }

      const data = await response.json();
      
      let result: PaginatedReservations;
      
      // Handle different response formats
      if (data.reservations && (data.pagination || data.total)) {
        result = {
          reservations: data.reservations,
          total: data.total || data.pagination?.total || 0,
          page: data.page || data.pagination?.page || 1,
          limit: data.limit || data.pagination?.limit || this.config.api.defaultPageSize,
          hasMore: data.hasNext || data.pagination?.hasMore || false,
        };
      } else if (Array.isArray(data)) {
        // Legacy format - assume all data returned
        result = {
          reservations: data,
          total: data.length,
          page: 1,
          limit: data.length,
          hasMore: false,
        };
      } else {
        console.warn('Unexpected reservations response format:', data);
        result = {
          reservations: [],
          total: 0,
          page: 1,
          limit: this.config.api.defaultPageSize,
          hasMore: false,
        };
      }
      
      // Cache the result with environment-specific TTL
      this.cache.set(cacheKey, result, this.config.cache.reservationsTTL);
      return result;
      
    } catch (error) {
      console.error('Error fetching customer reservations:', error);

      if (import.meta.env.DEV) {
        const mockReservations = getMockReservations(customerId);
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedMockData = mockReservations.slice(startIndex, endIndex);

        return {
          reservations: paginatedMockData,
          total: mockReservations.length,
          page,
          limit,
          hasMore: endIndex < mockReservations.length,
        };
      }

      return {
        reservations: [],
        total: 0,
        page,
        limit,
        hasMore: false,
      };
    }
  };

  // Debounced version for UI usage
  getCustomerReservationsDebounced = debounce(this.getCustomerReservations, this.config.debounce.apiDelay);

  // Get a specific reservation by ID with caching
  getReservation = async (reservationId: string, forceRefresh: boolean = false): Promise<Reservation | null> => {
    const cacheKey = `reservation_${reservationId}`;
    
    if (!forceRefresh) {
      const cached = this.cache.get<Reservation>(cacheKey);
      if (cached) {
        console.log('📦 Returning cached reservation:', reservationId);
        return cached;
      }
    }

    try {
      const response = await retryWithBackoff(
        () => this.fetchWithTimeout(`${getApiBase()}/reservations/${reservationId}`)
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch reservation: ${response.statusText}`);
      }

      const data = await response.json();
      const reservation = data.reservation || data;
      
      // Cache the reservation
      this.cache.set(cacheKey, reservation, this.config.cache.reservationsTTL);
      return reservation;
      
    } catch (error) {
      console.error('Error fetching reservation:', error);
      return null;
    }
  };

  // Batch operations for multiple reservations (B2B feature)
  batchUpdateReservations = async (updates: Array<{
    id: string;
    notes?: string;
    status?: string;
    version: number;
  }>): Promise<{ success: boolean; results: Array<{ id: string; success: boolean; error?: string }> }> => {
    if (updates.length > this.config.api.maxBatchSize) {
      throw new Error(`Batch size exceeds maximum of ${this.config.api.maxBatchSize}`);
    }

    try {
      const response = await retryWithBackoff(
        () => this.fetchWithTimeout(`${getApiBase()}/reservations/batch`, {
        method: 'PATCH',
          body: JSON.stringify({ updates }),
        })
      );

      if (!response.ok) {
        throw new Error(`Failed to batch update reservations: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Invalidate cache for updated reservations
      updates.forEach(update => {
        this.cache.invalidate(update.id);
      });
      
      return data;
      
    } catch (error) {
      console.error('Error batch updating reservations:', error);
      return {
        success: false,
        results: updates.map(update => ({
          id: update.id,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }))
      };
    }
  };

  // Cancel a reservation with optimistic locking
  cancelReservation = async (reservationId: string, version: number): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await retryWithBackoff(
        () => this.fetchWithTimeout(`${getApiBase()}/reservations/${reservationId}/cancel`, {
          method: 'PATCH',
          body: JSON.stringify({ version }),
        })
      );

      if (!response.ok) {
        if (response.status === 409) {
          return { success: false, error: 'Reservation was modified by another user. Please refresh and try again.' };
        }
        throw new Error(`Failed to cancel reservation: ${response.statusText}`);
      }

      // Invalidate cache
      this.cache.invalidate(reservationId);
      
      return { success: true };
      
    } catch (error) {
      console.error('Error canceling reservation:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  };

  // Update reservation notes with optimistic locking
  updateReservationNotes = async (
    reservationId: string, 
    notes: string, 
    version: number
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await retryWithBackoff(
        () => this.fetchWithTimeout(`${getApiBase()}/reservations/${reservationId}`, {
        method: 'PATCH',
          body: JSON.stringify({ notes, version }),
        })
      );

      if (!response.ok) {
        if (response.status === 409) {
          return { success: false, error: 'Reservation was modified by another user. Please refresh and try again.' };
        }
        throw new Error(`Failed to update reservation: ${response.statusText}`);
      }

      // Invalidate cache
      this.cache.invalidate(reservationId);
      
      return { success: true };
      
    } catch (error) {
      console.error('Error updating reservation notes:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  };

  // Batch cancel multiple reservations (B2B feature)
  batchCancelReservations = async (
    reservations: Array<{ id: string; version: number }>
  ): Promise<{ success: boolean; results: Array<{ id: string; success: boolean; error?: string }> }> => {
    if (reservations.length > this.config.api.maxBatchSize) {
      throw new Error(`Batch size exceeds maximum of ${this.config.api.maxBatchSize}`);
    }

    try {
      const response = await retryWithBackoff(
        () => this.fetchWithTimeout(`${getApiBase()}/reservations/batch/cancel`, {
          method: 'PATCH',
          body: JSON.stringify({ reservations }),
        })
      );

      if (!response.ok) {
        throw new Error(`Failed to batch cancel reservations: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Invalidate cache for all reservations
      reservations.forEach(reservation => {
        this.cache.invalidate(reservation.id);
      });
      
      return data;
      
    } catch (error) {
      console.error('Error batch canceling reservations:', error);
      return {
        success: false,
        results: reservations.map(reservation => ({
          id: reservation.id,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }))
      };
    }
  };

  // B2B specific methods
  getReservationsForMultipleCourses = async (
    courseIds: string[],
    dateRange: { start: Date; end: Date },
    page: number = 1,
    limit: number = this.config.api.defaultPageSize
  ): Promise<PaginatedReservations> => {
    const cacheKey = `multi_course_reservations_${courseIds.join(',')}_${dateRange.start.toISOString()}_${dateRange.end.toISOString()}_${page}_${limit}`;
    
    // Check cache first
    const cached = this.cache.get<PaginatedReservations>(cacheKey);
    if (cached) {
      console.log('📦 Returning cached multi-course reservations');
      return cached;
    }

    try {
      const queryParams = new URLSearchParams({
        courseIds: courseIds.join(','),
        startDate: dateRange.start.toISOString(),
        endDate: dateRange.end.toISOString(),
        page: page.toString(),
        limit: limit.toString(),
      });

      const response = await retryWithBackoff(
        () => this.fetchWithTimeout(`${getApiBase()}/reservations/multi-course?${queryParams}`)
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch multi-course reservations: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Cache with shorter TTL for B2B data
      this.cache.set(cacheKey, data, this.config.cache.reservationsTTL);
      return data;
      
    } catch (error) {
      console.error('Error fetching multi-course reservations:', error);
      return {
        reservations: [],
        total: 0,
        page: 1,
        limit,
        hasMore: false,
      };
    }
  };

  // Compatibility methods for existing booking service
  getDefaultGuestCustomerType = async (): Promise<string | null> => {
    try {
      // Use public endpoint - no auth required
      // Get club identifiers from query + path slug (matches OnlineBookingEngine).
      const clubParams = publicBookingApiParamsFromWindow();
      if ([...clubParams.keys()].length === 0) {
        return null;
      }
      const customerTypesUrl = `/customer-types/public/list?${clubParams.toString()}`;
      console.log('🔍 [DEFAULT GUEST TYPE] Fetching from URL:', customerTypesUrl);
      const response = await this.fetchWithTimeout(customerTypesUrl);
      const customerTypes = await response.json();
      
      // Find the default guest customer type (usually the first guest type or one marked as default)
      const guestTypes = customerTypes.filter((ct: any) =>
        customerTypeIsPublicGuestOnlineBooking(ct) && customerTypeCanBookOnline(ct),
      );
      
      if (guestTypes.length > 0) {
        guestTypes.sort((a: { name?: string }, b: { name?: string }) =>
          String(a.name ?? '').localeCompare(String(b.name ?? '')),
        );
        return guestTypes[0].id;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching default guest customer type:', error);
      return null;
    }
  };

  getTeeTimeAvailability = async (
    date: Date, 
    players: number, 
    filters: {
      timeOfDay: string;
      roundType: string;
      courseIds: string[];
      /** Bare id string, or `{ id }` from CRM — normalized inside `getTeeTimeAvailability`. */
      customerTypeId: string | { id: string } | '' | undefined;
    },
    includeMemberInfo: boolean = false,
    showAllReservations: boolean = false
  ) => {
    try {
      const rawCt = filters.customerTypeId as unknown;
      const customerTypeIdFilter =
        typeof rawCt === 'string' && rawCt.trim()
          ? rawCt.trim()
          : rawCt &&
              typeof rawCt === 'object' &&
              rawCt !== null &&
              'id' in rawCt &&
              typeof (rawCt as { id: unknown }).id === 'string'
            ? String((rawCt as { id: string }).id).trim()
            : '';

      // Format date for API
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      // Use provided courseIds directly - the OnlineBookingEngine already fetches courses with the correct clubId
      // Don't re-fetch courses here as the public endpoint requires clubId/clubNumber
      const courseIds = filters.courseIds;
      console.log(`🔍 Using courses for ${filters.roundType}:`, courseIds);
      
      if (courseIds.length === 0) {
        throw new Error('No courses available - please ensure courses are loaded before fetching tee times');
      }
      
      // Fetch tee times for all selected courses
      // Include reservations with player data if we need member info
      const includeReservations = includeMemberInfo ? '&includeReservations=true' : '';
      const allTeeTimes = [];
      for (const courseId of courseIds) {
        const response = await this.fetchWithTimeout(`/tee-time-generation/tee-times?date=${dateStr}&courseId=${courseId}${includeReservations}&_t=${Date.now()}&_cache=no-cache`);
        const rawText = await response.text();
        let teeTimes: unknown;
        try {
          teeTimes = rawText.trim() ? JSON.parse(rawText) : [];
        } catch {
          throw new Error('Invalid JSON from tee-time-generation/tee-times');
        }
        if (!response.ok) {
          const body = teeTimes as Record<string, unknown>;
          const msg =
            typeof body?.message === 'string'
              ? body.message
              : typeof body?.error === 'string'
                ? body.error
                : `Tee times request failed (${response.status})`;
          throw new Error(msg);
        }
        if (!Array.isArray(teeTimes)) {
          console.error('🔍 [API RESPONSE] Expected array, got:', teeTimes);
          throw new Error('Tee times response is not an array');
        }
        console.log(`🔍 [API RESPONSE] Fetched ${teeTimes.length} tee times for course ${courseId}`);
        // Check if any tee times have reservations
        const teeTimesWithReservations = teeTimes.filter((tt: any) => tt.reservations && tt.reservations.length > 0);
        console.log(`🔍 [API RESPONSE] ${teeTimesWithReservations.length} tee times have reservations`);
        if (teeTimesWithReservations.length > 0) {
          console.log(`🔍 [API RESPONSE] Sample tee time with reservations:`, {
            time: teeTimesWithReservations[0].time,
            reservationCount: teeTimesWithReservations[0].reservations?.length,
            firstReservation: teeTimesWithReservations[0].reservations?.[0] ? {
              id: teeTimesWithReservations[0].reservations[0].id,
              playerCount: teeTimesWithReservations[0].reservations[0].playerCount,
              hasPlayers: !!teeTimesWithReservations[0].reservations[0].players,
              playersCount: teeTimesWithReservations[0].reservations[0].players?.length || 0,
              hasPlayerSlots: !!teeTimesWithReservations[0].reservations[0].playerSlots,
              playerSlotsCount: teeTimesWithReservations[0].reservations[0].playerSlots?.length || 0
            } : null
          });
        }
        allTeeTimes.push(...teeTimes.map((tt: any) => ({ ...tt, courseId })));
      }

      // Get customer types for online booking - use public endpoint
      const bookingClubParams = publicBookingApiParamsFromWindow();
      if ([...bookingClubParams.keys()].length === 0) {
        throw new Error('Club context is required to load tee times');
      }

      const coursePolicyById = await this.getCoursePoliciesByClubParams(bookingClubParams);

      const customerTypesUrl = `/customer-types/public/list?${bookingClubParams.toString()}`;
      console.log('🔍 [CUSTOMER TYPES] Fetching from URL:', customerTypesUrl);
      const customerTypesResponse = await this.fetchWithTimeout(customerTypesUrl);
      const customerTypes = await customerTypesResponse.json();
      console.log('🔍 [CUSTOMER TYPES] Received customer types:', customerTypes.length, customerTypes);
      console.log('🔍 [CUSTOMER TYPES] Full details:', customerTypes.map((ct: any) => ({ 
        id: ct.id, 
        name: ct.name, 
        category: ct.category 
      })));
      
      // Find guest and member customer types (member-facing names never offered as public guest rates)
      const guestCustomerTypes = customerTypes.filter((ct: any) =>
        customerTypeIsPublicGuestOnlineBooking(ct) && customerTypeCanBookOnline(ct),
      );

      const memberCustomerTypes = customerTypes.filter((ct: any) =>
        customerTypeIsMemberFacing(ct),
      );
      
      console.log('🔍 [CUSTOMER TYPES] Guest types found:', guestCustomerTypes.length, guestCustomerTypes.map((ct: any) => ({ id: ct.id, name: ct.name, category: ct.category })));
      console.log('🔍 [CUSTOMER TYPES] Member types found:', memberCustomerTypes.length, memberCustomerTypes.map((ct: any) => ({ id: ct.id, name: ct.name, category: ct.category })));
      
      if (guestCustomerTypes.length === 0) {
        console.error('❌ [CUSTOMER TYPES] No online-bookable guest customer types found! Available types:', customerTypes.map((ct: any) => ({ name: ct.name, category: ct.category, canBookOnline: ct.canBookOnline })));
        throw new Error('Online booking is not available for public guest rates at this club. Please contact the club.');
      }

      // Public pricing + slot promos are per course. A single request with courseIds[0] breaks
      // multi-course searches (wrong prices and missing slotPromotion for other courses).
      const pricingBySlotIndex = new Map<number, any>();
      const courseIdsForPricing = [
        ...new Set(allTeeTimes.map((tt: any) => String(tt.courseId))),
      ];

      for (const cid of courseIdsForPricing) {
        const slotIndices: number[] = [];
        const times: string[] = [];
        const laneKeys: string[] = [];
        allTeeTimes.forEach((tt: any, idx: number) => {
          if (String(tt.courseId) !== cid) return;
          slotIndices.push(idx);
          times.push(tt.time);
          laneKeys.push(
            tt.laneKey && String(tt.laneKey).trim() !== ''
              ? tt.laneKey
              : 'default'
          );
        });
        if (times.length === 0) continue;

        const pricingBody: Record<string, unknown> = {
          courseId: cid,
          date: dateStr,
          times,
          laneKeys,
          roundType: filters.roundType,
          ...Object.fromEntries(bookingClubParams.entries()),
        };

        console.log(
          '🔍 [PRICING] Requesting pricing for course',
          cid,
          'slots:',
          times.length
        );
        const pricingResponse = await this.fetchWithTimeout(
          '/pricing/public/tee-times',
          {
            method: 'POST',
            headers: this.getAuthHeaders(),
            body: JSON.stringify(pricingBody),
          }
        );

        const pricingData = await pricingResponse.json();
        if (!Array.isArray(pricingData)) {
          console.warn('⚠️ [PRICING] Non-array response for course', cid);
          continue;
        }
        pricingData.forEach((row: any, j: number) => {
          const globalIdx = slotIndices[j];
          if (globalIdx !== undefined) {
            pricingBySlotIndex.set(globalIdx, row);
          }
        });
      }

      const sampleRow = pricingBySlotIndex.get(0);
      if (sampleRow) {
        console.log(
          '🔍 [PRICING] Sample row (index 0):',
          JSON.stringify(sampleRow, null, 2)
        );
      }

      // Check if any tee times have pricing data
      const hasAnyPricingRules = [...pricingBySlotIndex.values()].some(
        (pricing: any) =>
          pricing?.pricing && Object.keys(pricing.pricing).length > 0
      );
      console.log('🔍 [PRICING] Has any pricing rules:', hasAnyPricingRules);
      if (!hasAnyPricingRules) {
        console.warn('⚠️ [PRICING] No pricing rules found, returning empty array');
        return []; // Return empty array if no pricing data exists
      }

      // Build a map of course IDs to names from the allTeeTimes data (if available)
      // The tee times may contain course name from the tee-time-generation endpoint
      const courseNameMap: Record<string, string> = {};
      allTeeTimes.forEach((tt: any) => {
        if (tt.courseName && tt.courseId) {
          courseNameMap[tt.courseId] = tt.courseName;
        }
      });
      
      // Fallback: use course ID as name if not found
      const courseNames = courseIds.map((id: string) => {
        return courseNameMap[id] || `Course ${id.slice(-4)}`;
      });

      // Transform the response to match the expected interface
      const teeTimes = allTeeTimes
        .map((tt: any, slotIndex: number) => ({ tt, slotIndex }))
        .filter(({ tt }) => {
          // Apply filters
          if (filters.timeOfDay) {
            const hour = parseInt(tt.time.split(':')[0]);
            const timeOfDay = this.getTimeOfDay(hour);
            if (timeOfDay !== filters.timeOfDay) {
              return false;
            }
          }
          
          // Skip availability checks if showing all reservations (for tee sheet view)
          if (!showAllReservations) {
            if (tt.isAvailable === false) {
              return false;
            }
            
            const availableSpots = tt.maxGolfers - (tt.bookedSpots || 0);
            const bookedCount = tt.bookedSpots || 0;
            
            // Check if tee time is blocked or fully booked
            if (availableSpots === 0 && bookedCount === 0) {
              return false;
            }
            
            if (availableSpots === 0 && bookedCount > 0) {
              return false;
            }
            
            if (availableSpots < players) {
      return false;
            }
          }
          
          return true;
        })
        .map(({ tt, slotIndex }) => {
          console.log('🔍 Processing tee time:', tt.time, 'has reservations:', !!tt.reservations, 'reservations length:', tt.reservations?.length || 0);
          
          // Create a combined dateTime from date and time fields
          const [year, month, day] = tt.date.split('-');
          const [hours, minutes] = tt.time.split(':');
          const dateTime = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
          dateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
          
          const teeTimePricing = pricingBySlotIndex.get(slotIndex);
          /** Per-player half cart when course.preloadHalfCartWithGreenFee (API: mandatoryHalfCartPreload). */
          const mandatoryCartAdd =
            teeTimePricing?.mandatoryHalfCartPreload &&
            typeof teeTimePricing.halfCartAddon === 'number'
              ? Number(teeTimePricing.halfCartAddon)
              : 0;

          // Get pricing options based on user type
          let lowestPrice: number | null = null;
          let allPricingOptions: Array<{
            customerTypeId: string;
            customerTypeName: string;
            price: number;
            color: string;
          }> = [];
          let dynamicPricing:
            | {
                direction: 'lower' | 'higher';
                percentRounded: number;
                underOnePercent: boolean;
              }
            | undefined;
          let cardDisplayCustomerTypeId: string | undefined;

          const staffPromotion =
            normalizeStaffPromotion(tt.promotion) ??
            normalizeStaffPromotion(teeTimePricing?.slotPromotion);

          if (teeTimePricing) {
            const isAuthenticatedMember =
              !!customerTypeIdFilter &&
              memberCustomerTypes.some((ct: any) => ct.id === customerTypeIdFilter);

            if (isAuthenticatedMember && memberCustomerTypes.length > 0) {
              console.log('🔍 [PRICING] Authenticated member booking — type id:', customerTypeIdFilter);
              for (const memberType of memberCustomerTypes) {
                const pricing = teeTimePricing.pricing[memberType.id];
                if (pricing && pricing.template) {
                  const display = Number(pricing.finalPrice) + mandatoryCartAdd;
                  allPricingOptions.push({
                    customerTypeId: memberType.id,
                    customerTypeName: memberType.name,
                    price: display,
                    color: memberType.color || '#000000',
                  });
                }
              }

              const myRow = teeTimePricing.pricing[customerTypeIdFilter];
              if (myRow?.template) {
                lowestPrice = Number(myRow.finalPrice) + mandatoryCartAdd;
                cardDisplayCustomerTypeId = customerTypeIdFilter;
                dynamicPricing = buildDynamicPricingImpact(myRow);
              } else {
                const memberPrices = memberCustomerTypes
                  .map((memberType: any) => {
                    const pricing = teeTimePricing.pricing[memberType.id];
                    if (pricing && pricing.template) {
                      return Number(pricing.finalPrice) + mandatoryCartAdd;
                    }
                    return null;
                  })
                  .filter((price: number | null): price is number => price !== null);

                if (memberPrices.length > 0) {
                  // Held in a const so the closure below sees `number`, not `number | null`.
                  const lowestMemberPrice = Math.min(...memberPrices);
                  lowestPrice = lowestMemberPrice;
                  console.log('🔍 [PRICING] Member type had no template; using lowest member rate:', lowestMemberPrice);
                  const selectedMemberPricing = pickPricingEntryForPrice(
                    teeTimePricing,
                    memberCustomerTypes,
                    lowestMemberPrice - mandatoryCartAdd,
                  );
                  dynamicPricing = buildDynamicPricingImpact(selectedMemberPricing);
                  const matchCt = memberCustomerTypes.find((ct: any) => {
                    const p = teeTimePricing.pricing[ct.id];
                    return (
                      p?.template &&
                      Math.abs(Number(p.finalPrice) + mandatoryCartAdd - lowestMemberPrice) < 0.02
                    );
                  });
                  cardDisplayCustomerTypeId = matchCt?.id;
                }
              }
            } else {
              console.log('🔍 [PRICING] Processing guest pricing options for tee time:', tt.time);
              console.log(
                '🔍 [PRICING] Guest customer types to check:',
                guestCustomerTypes.map((ct: any) => ({ id: ct.id, name: ct.name })),
              );
              console.log(
                '🔍 [PRICING] Available pricing keys in response:',
                Object.keys(teeTimePricing.pricing || {}),
              );

              const prices: number[] = [];
              for (const guestType of guestCustomerTypes) {
                const pricing = teeTimePricing.pricing[guestType.id];
                console.log(
                  `🔍 [PRICING] Checking guest type ${guestType.name} (${guestType.id}):`,
                  pricing ? 'FOUND' : 'NOT FOUND',
                );
                if (pricing && pricing.template) {
                  const display = Number(pricing.finalPrice) + mandatoryCartAdd;
                  allPricingOptions.push({
                    customerTypeId: guestType.id,
                    customerTypeName: guestType.name,
                    price: display,
                    color: guestType.color || '#000000',
                  });
                  prices.push(display);
                }
              }

              console.log('🔍 [PRICING] Guest prices found:', prices);

              const preferredGuestId = coursePolicyById[tt.courseId]?.defaultGuestTypeId ?? null;
              const prefRow =
                preferredGuestId && teeTimePricing.pricing[preferredGuestId]
                  ? teeTimePricing.pricing[preferredGuestId]
                  : null;

              if (prefRow?.template) {
                lowestPrice = Number(prefRow.finalPrice) + mandatoryCartAdd;
                cardDisplayCustomerTypeId = preferredGuestId ?? undefined;
                dynamicPricing = buildDynamicPricingImpact(prefRow);
                console.log(
                  '🔍 [PRICING] Using course default guest rate:',
                  preferredGuestId,
                  lowestPrice,
                );
              } else if (prices.length > 0) {
                // Held in a const so the closure below sees `number`, not `number | null`.
                const lowestGuestPrice = Math.min(...prices);
                lowestPrice = lowestGuestPrice;
                console.log('🔍 [PRICING] No default guest template; lowest guest price:', lowestGuestPrice);
                const selectedGuestPricing = pickPricingEntryForPrice(
                  teeTimePricing,
                  guestCustomerTypes,
                  lowestGuestPrice - mandatoryCartAdd,
                );
                dynamicPricing = buildDynamicPricingImpact(selectedGuestPricing);
                const matchCt = guestCustomerTypes.find((ct: any) => {
                  const p = teeTimePricing.pricing[ct.id];
                  return (
                    p?.template &&
                    Math.abs(Number(p.finalPrice) + mandatoryCartAdd - lowestGuestPrice) < 0.02
                  );
                });
                cardDisplayCustomerTypeId = matchCt?.id;
              } else {
                console.warn('⚠️ [PRICING] No guest prices found for tee time:', tt.time);
              }
            }
          }

          const dynamicPricingForCard = staffPromotion ? undefined : dynamicPricing;
          
          // Find the course name for this tee time
          const courseName = courseNames[courseIds.indexOf(tt.courseId)] || 'Unknown Course';
          
          // Get member player information if requested
          let memberPlayers: Array<{
            spotNumber: number;
            firstName: string;
            lastName: string;
            customerType: string;
          }> = [];
          
          console.log(`🔍 [MEMBER PLAYERS] includeMemberInfo=${includeMemberInfo}, hasReservations=${!!tt.reservations}, reservationCount=${tt.reservations?.length || 0}`);
          
          if (includeMemberInfo && tt.reservations && tt.reservations.length > 0) {
            const memberTypeIds = memberCustomerTypes.map((ct: any) => ct.id);
            console.log('🔍 [MEMBER PLAYERS] Processing reservations for tee time:', tt.time);
            console.log('🔍 [MEMBER PLAYERS] Member type IDs:', memberTypeIds);
            console.log('🔍 [MEMBER PLAYERS] Member customer types:', memberCustomerTypes.map((ct: any) => ({ id: ct.id, name: ct.name, category: ct.category })));
            console.log('🔍 [MEMBER PLAYERS] Reservations count:', tt.reservations.length);
            
            // Extract member players from reservations
            tt.reservations.forEach((reservation: any) => {
              const players = reservationRosterPlayers(reservation);
              console.log('🔍 Reservation players:', JSON.stringify(players, null, 2));
              
              players.forEach((player: any) => {
                if (player.customer) {
                  const c = player.customer as Record<string, unknown> & {
                    firstName?: string;
                    lastName?: string;
                    first_name?: string;
                    last_name?: string;
                    customerType?: unknown;
                    customerTypeId?: string;
                    showNameOnTeeSheet?: boolean;
                  };
                  const fn = String(c.firstName ?? c.first_name ?? '').trim();
                  const ln = String(c.lastName ?? c.last_name ?? '').trim();

                  let customerTypeId: string;
                  if (typeof c.customerType === 'object' && c.customerType !== null) {
                    customerTypeId = String((c.customerType as { id?: string }).id ?? '');
                  } else {
                    customerTypeId =
                      String(c.customerType ?? '') || String(c.customerTypeId ?? '');
                  }

                  let typeForGating: { category?: string | null; name?: string | null } | null =
                    null;
                  if (typeof c.customerType === 'object' && c.customerType !== null) {
                    const o = c.customerType as { category?: string; name?: string };
                    typeForGating = {
                      category: o.category ?? null,
                      name: o.name ?? null,
                    };
                  } else if (customerTypeId) {
                    const rec = customerTypes.find((ct: any) => ct.id === customerTypeId);
                    typeForGating = rec
                      ? { category: rec.category ?? null, name: rec.name ?? null }
                      : null;
                  }

                  const showIndividualName =
                    typeForGating != null
                      ? customerTypeShowsIndividualNameOnTeeSheet(typeForGating)
                      : memberTypeIds.includes(customerTypeId);

                  console.log(`🔍 Player ${player.spotNumber}: customerTypeId=${customerTypeId}, showIndividualName=${showIndividualName}`, {
                    customer: `${fn} ${ln}`.trim(),
                    customerType: c.customerType,
                    customerTypeId,
                  });

                  if (showIndividualName) {
                    let customerTypeName = 'Member';
                    if (typeof c.customerType === 'object' && c.customerType !== null) {
                      const nm = (c.customerType as { name?: string }).name;
                      if (nm) customerTypeName = nm;
                    } else if (customerTypeId) {
                      customerTypeName =
                        customerTypes.find((ct: any) => ct.id === customerTypeId)?.name ||
                        'Member';
                    }

                    const showName = c.showNameOnTeeSheet !== false;

                    memberPlayers.push({
                      spotNumber: player.spotNumber,
                      firstName: showName ? fn : 'Member',
                      lastName: showName ? ln : '',
                      customerType: customerTypeName,
                    });

                    console.log(
                      `✅ Added member player: ${showName ? `${fn} ${ln}`.trim() : 'Member'} at spot ${player.spotNumber}`,
                    );
                  } else {
                    // Non-member customer (e.g., student, senior, etc.) - show customer type name, not individual name
                    let customerTypeName = 'Guest';
                    if (typeof player.customer.customerType === 'object' && player.customer.customerType?.name) {
                      customerTypeName = player.customer.customerType.name;
                    } else if (customerTypeId) {
                      customerTypeName = customerTypes.find((ct: any) => ct.id === customerTypeId)?.name || 'Guest';
                    }
                    
                    // For non-members, always show customer type name (e.g., "Student", "Senior") instead of individual name
                    memberPlayers.push({
                      spotNumber: player.spotNumber,
                      firstName: customerTypeName,
                      lastName: '',
                      customerType: customerTypeName
                    });
                    
                    console.log(`✅ Added non-member customer player: ${customerTypeName} at spot ${player.spotNumber}`);
                  }
                } else if (player.isGuest || player.guestName) {
                  // Include guests - always show as "Guest"
                  memberPlayers.push({
                    spotNumber: player.spotNumber,
                    firstName: 'Guest',
                    lastName: '',
                    customerType: 'Guest'
                  });
                  console.log(`✅ Added guest player at spot ${player.spotNumber}`);
                }
              });
            });
            
            console.log('🔍 Final memberPlayers array:', memberPlayers);
            if (memberPlayers.length > 0) {
              console.log(`🔍 [MEMBER PLAYERS] Tee time ${tt.time} - spotNumbers:`, memberPlayers.map(mp => mp.spotNumber));
            }
          }

          // API also exposes `playerNames` (per-spot strings from tee-time-generation). Use when reservation
          // player objects did not map (e.g. shape mismatch) but the slot shows as booked.
          if (
            includeMemberInfo &&
            memberPlayers.length === 0 &&
            (tt.bookedSpots || 0) > 0 &&
            Array.isArray((tt as { playerNames?: unknown }).playerNames)
          ) {
            const rawNames = (tt as { playerNames: unknown[] }).playerNames;
            rawNames.forEach((name: unknown, i: number) => {
              const trimmed = String(name ?? '').trim();
              if (!trimmed) return;
              const parts = trimmed.split(/\s+/);
              const firstName = parts[0] ?? '';
              const lastName = parts.slice(1).join(' ').trim();
              memberPlayers.push({
                spotNumber: i + 1,
                firstName,
                lastName,
                customerType: 'Booked',
              });
            });
            if (memberPlayers.length > 0) {
              console.log(`🔍 [MEMBER PLAYERS] Fallback from playerNames for ${tt.time}:`, memberPlayers);
            }
          }
          
          // Calculate accurate availability using database bookedSpots
          const actualBookedCount = tt.bookedSpots || 0;
          const actualAvailableSpots = Math.max(0, (tt.maxGolfers || 4) - actualBookedCount);

          const laneSuffix =
            tt.laneKey && tt.laneKey !== 'default' ? `|${tt.laneKey}` : '';
          const reservationTeeTimeId = buildReservationTeeTimeIdForApi({
            date: tt.date,
            time: tt.time,
            laneKey: tt.laneKey,
          });
          const meta = coursePolicyById[tt.courseId] || {
            preloadHalfCartWithGreenFee: false,
          };
          const pricingMand = teeTimePricing?.mandatoryHalfCartPreload;
          const metaPreload = !!meta.preloadHalfCartWithGreenFee;
          const mandatoryHalfCartPreload =
            pricingMand === true ? true : pricingMand === false ? false : metaPreload;

          return {
            id: `${tt.courseId}-${tt.date}T${tt.time}${laneSuffix}`,
            courseName: courseName,
            courseId: tt.courseId,
            dateTime: dateTime,
            originalDate: tt.date,
            reservationTeeTimeId,
            price: lowestPrice || 0,
            customerTypeId: cardDisplayCustomerTypeId,
            capacity: tt.maxGolfers || 4,
            bookedCount: actualBookedCount,
            availableSpots: actualAvailableSpots,
            holeSet: tt.holeSet, // Add holeSet field from the raw tee time data
            pricingOptions: allPricingOptions,
            memberPlayers: memberPlayers.length > 0 ? memberPlayers : undefined,
            dynamicPricing: dynamicPricingForCard,
            promotion: staffPromotion ?? undefined,
            courseDefaultGuestTypeId: meta.defaultGuestTypeId || undefined,
            coursePreloadHalfCartWithGreenFee: metaPreload,
            mandatoryHalfCartPreload,
            /** Same basis as displayed $ amounts — card copy must not use stale course flags alone. */
            priceIncludesHalfCartAddon: mandatoryCartAdd > 0,
          };
        })
        .sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());

      return teeTimes;
    } catch (error: any) {
      console.error('Error fetching tee time availability:', error);
      if (error.response?.status === 400) {
        throw new Error('Please select a date and course to search for tee times');
      }
      throw new Error('Failed to fetch available tee times. Please try again.');
    }
  };

  // Helper method to determine time of day
  private getTimeOfDay(hour: number): string {
    if (hour < 12) return 'morning';
    return 'afternoon';
  }

  /**
   * Create a customer message for a reservation
   */
  createMessage = async (reservationId: string, messageData: { message: string }) => {
    try {
      // Security: Verify authentication before attempting to send message
      const sessionData = localStorage.getItem('booking_service_auth');
      if (!sessionData) {
        throw new Error('Authentication required to send messages. Please log in first.');
      }

      const session = JSON.parse(sessionData);
      if (!session.token) {
        throw new Error('Invalid authentication session. Please log in again.');
      }

      // Security: Check if session is expired
      if (Date.now() > session.expiresAt) {
        throw new Error('Your session has expired. Please log in again to send messages.');
      }

      // Security: Validate message content
      if (!messageData.message || messageData.message.trim().length === 0) {
        throw new Error('Message cannot be empty');
      }

      if (messageData.message.length > 500) {
        throw new Error('Message exceeds maximum length of 500 characters');
      }

      // Security: Sanitize message content
      const sanitizedMessage = messageData.message
        .trim()
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/on\w+\s*=/gi, '');

      if (!sanitizedMessage) {
        throw new Error('Message contains invalid content');
      }

      console.log('🔐 Sending authenticated message for reservation:', reservationId);
      // Send to reservations message endpoint (persists in notes parsed by admin inbox)
      const response = await this.fetchWithTimeout(`/reservations/${reservationId}/message`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ message: sanitizedMessage })
      });

      if (!response.ok) {
        const fallback =
          response.status === 401
            ? 'Authentication failed. Please log in again.'
            : response.status === 403
              ? 'You are not authorized to send messages for this reservation.'
              : 'Failed to send message';
        const { message, code } = await parseFailedResponseBody(response, fallback);
        throw new ReservationsApiError(message, response.status, code);
      }

      const result = await response.json();
      console.log('✅ Message sent successfully with authentication');
      return result;
    } catch (error) {
      console.error('❌ Secure message creation failed:', error);
      throw error;
    }
  };

  createReservation = async (reservationData: any) => {
    try {
      const clubContext = publicBookingApiParamsObjectFromWindow();
      const payload = { ...clubContext, ...reservationData };
      console.log('Creating reservation with data:', payload);
      const response = await this.fetchWithTimeout('/reservations', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const { message, code } = await parseFailedResponseBody(
          response,
          `Booking could not be completed (${response.status}). Please try again.`
        );
        throw new ReservationsApiError(message, response.status, code);
      }
      
      const result = await response.json();
      console.log('Reservation created successfully:', result);
      return result;
    } catch (error: unknown) {
      console.error('Failed to create reservation:', error);
      if (error instanceof ReservationsApiError) {
        throw error;
      }
      const err = error as { name?: string; message?: string };
      if (err?.name === 'AbortError' || err?.message?.includes('timeout') || err?.message?.includes('network')) {
        console.warn('Reservation request timed out or failed due to network issues');
        throw new Error('Reservation request timed out. Please try again.');
      }
      throw error;
    }
  };

  // B2C specific methods
  createGuestReservation = async (
    teeTimeId: string,
    guestInfo: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
    },
    playerCount: number,
    notes?: string
  ): Promise<Reservation> => {
    try {
      const response = await retryWithBackoff(
        () => this.fetchWithTimeout(`${getApiBase()}/reservations/guest`, {
          method: 'POST',
          body: JSON.stringify({
            teeTimeId,
            guestInfo,
            playerCount,
            notes,
            source: 'online',
            channel: 'booking-widget'
          }),
        })
      );

      if (!response.ok) {
        const { message, code } = await parseFailedResponseBody(
          response,
          `Booking could not be completed (${response.status}). Please try again.`
        );
        throw new ReservationsApiError(message, response.status, code);
      }

      const data = await response.json();
      
      // Invalidate relevant caches
      console.log('🧹 Invalidating caches after reservation creation');
      this.cache.invalidate('tee_times');
      this.cache.invalidate('reservations');
      console.log('✅ Caches invalidated');
      
      return data.reservation;
      
    } catch (error) {
      console.error('Error creating guest reservation:', error);
      throw error;
    }
  };

  // Check tee time availability (B2C feature)
  checkTeeTimeAvailability = async (
    teeTimeId: string,
    playerCount: number
  ): Promise<{ available: boolean; spots: number; message?: string }> => {
    const cacheKey = `availability_${teeTimeId}_${playerCount}`;
    
    // Check cache first (short TTL for real-time data)
    const cached = this.cache.get<{ available: boolean; spots: number; message?: string }>(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const response = await this.fetchWithTimeout(`${getApiBase()}/tee-times/${teeTimeId}/availability?playerCount=${playerCount}`);

      if (!response.ok) {
        throw new Error(`Failed to check availability: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Cache with very short TTL for real-time data
      this.cache.set(cacheKey, data, this.config.cache.teeTimesTTL);
      return data;
      
    } catch (error) {
      console.error('Error checking tee time availability:', error);
      return { available: false, spots: 0, message: 'Unable to check availability' };
    }
  };

  // Clear cache (useful for manual refresh)
  clearCache = (): void => {
    this.cache.clear();
  };

  // Invalidate specific customer's cache
  invalidateCustomerCache = (customerId: string): void => {
    this.cache.invalidate(customerId);
  };

  /** After cancel/edit so dashboard history shows fresh `status` (list + optional single-reservation cache). */
  invalidateCustomerReservationMutation = (customerId: string, reservationId?: string): void => {
    this.invalidateCustomerCache(customerId);
    if (reservationId) {
      this.cache.invalidate(reservationId);
    }
  };

  invalidateMyReservationsCache = (): void => {
    this.cache.invalidate('my_reservations_');
  };

  // Get cache statistics (for debugging)
  getCacheStats = (): { size: number; keys: string[] } => {
    return {
      size: this.cache['cache'].size,
      keys: Array.from(this.cache['cache'].keys())
    };
  };
}

// Create singleton instance
export const reservationsService = new OptimizedReservationsService();

// Mock data for development/testing with version numbers added
const getMockReservations = (customerId: string): Reservation[] => {
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  return [
    {
      id: 'mock-reservation-1',
      teeTimeId: tomorrow.toISOString(),
      courseId: 'mock-course-1',
      customerId: customerId,
      playerCount: 2,
      status: 'reserved',
      notes: 'Early morning round',
      source: 'online',
      channel: 'booking-widget',
      version: 1,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
      course: {
        id: 'mock-course-1',
        name: 'Championship Course'
      },
      players: [
        {
          id: 'player-1',
          spotNumber: 1,
          isGuest: false,
          customer: {
            id: customerId,
            firstName: 'John',
            lastName: 'Doe'
          }
        },
        {
          id: 'player-2',
          spotNumber: 2,
          isGuest: true,
          guestName: 'Jane Smith'
        }
      ]
    },
    {
      id: 'mock-reservation-2',
      teeTimeId: nextWeek.toISOString(),
      courseId: 'mock-course-2',
      customerId: customerId,
      playerCount: 4,
      status: 'reserved',
      source: 'online',
      channel: 'booking-widget',
      version: 1,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
      course: {
        id: 'mock-course-2',
        name: 'Executive Course'
      },
      players: [
        {
          id: 'player-3',
          spotNumber: 1,
          isGuest: false,
          customer: {
            id: customerId,
            firstName: 'John',
            lastName: 'Doe'
          }
        },
        {
          id: 'player-4',
          spotNumber: 2,
          isGuest: true,
          guestName: 'Bob Johnson'
        },
        {
          id: 'player-5',
          spotNumber: 3,
          isGuest: true,
          guestName: 'Alice Brown'
        },
        {
          id: 'player-6',
          spotNumber: 4,
          isGuest: true,
          guestName: 'Charlie Wilson'
        }
      ]
    },
    {
      id: 'mock-reservation-3',
      teeTimeId: lastWeek.toISOString(),
      courseId: 'mock-course-1',
      customerId: customerId,
      playerCount: 1,
      status: 'completed',
      source: 'online',
      channel: 'booking-widget',
      version: 1,
      createdAt: lastWeek.toISOString(),
      updatedAt: lastWeek.toISOString(),
      course: {
        id: 'mock-course-1',
        name: 'Championship Course'
      },
      players: [
        {
          id: 'player-7',
          spotNumber: 1,
          isGuest: false,
          customer: {
            id: customerId,
            firstName: 'John',
            lastName: 'Doe'
          }
        }
      ]
    },
    {
      id: 'mock-reservation-4',
      teeTimeId: new Date(lastWeek.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      courseId: 'mock-course-2',
      customerId: customerId,
      playerCount: 2,
      status: 'canceled',
      source: 'online',
      channel: 'booking-widget',
      version: 2,
      createdAt: new Date(lastWeek.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date(lastWeek.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      course: {
        id: 'mock-course-2',
        name: 'Executive Course'
      },
      players: [
        {
          id: 'player-8',
          spotNumber: 1,
          isGuest: false,
          customer: {
            id: customerId,
            firstName: 'John',
            lastName: 'Doe'
          }
        },
        {
          id: 'player-9',
          spotNumber: 2,
          isGuest: true,
          guestName: 'Mike Davis'
        }
      ]
    }
  ];
};